import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronRight, CreditCard, LockKeyhole, ReceiptText, ShieldCheck, ShoppingBag, Sparkles } from "lucide-react";

import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { getMe } from "@/lib/karma/storage";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type PackageKey = "pin" | "isim" | "normal" | "premium" | "iliski" | "test";

type CatalogResponse = {
  ok: boolean;
  packages: Array<{
    id: string;
    key: PackageKey;
    name: string;
    description: string;
    basePriceTry: number;
    discountedPriceTry?: number;
    requiresBirthDate: boolean;
    requiresPartner: boolean;
  }>;
  products: Array<{
    id: string;
    sku: string;
    name: string;
    description: string;
    category: string;
    priceTry: number;
    discountedPriceTry?: number;
    tags: string[];
  }>;
  settings: {
    payment: {
      ibanLabel: string;
      iban: string;
      accountName: string;
      note: string;
    };
  };
};

type CreateOrderResponse = {
  ok: boolean;
  order: { id: string; status: "pending" | "paid" | "cancelled"; totalTry: number };
  access: { orderId: string; token: string };
};

type CartItem = {
  packageKey: string;
  qty: number;
};

type ProductCartItem = {
  productId: string;
  qty: number;
};

type SavedAddressLabel = "ev" | "is" | "diger";

type SavedAddress = {
  id: string;
  label: SavedAddressLabel;
  title: string;
  identity_number: string;
  phone: string;
  address_line1: string;
  city: string;
  district: string;
  postcode: string;
};

const GOLD = "#d4af37";
const CART_STORAGE_KEY = "numeroloji_cart_v1";
const PRODUCT_CART_STORAGE_KEY = "numeroloji_product_cart_v1";
const PROFILE_EXTRAS_STORAGE_KEY = "panel_profile_extras_v1";
const VAT_RATE = 0.2;

function safeParseCart(raw: string | null): CartItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((it) => {
        const obj = it as any;
        if (obj?.digitalContent) return { digitalContent: obj.digitalContent } as any;
        const packageKey = typeof obj?.packageKey === "string" ? obj.packageKey : null;
        const qty = typeof obj?.qty === "number" ? obj.qty : Number(obj?.qty);
        if (!packageKey) return null;
        if (!Number.isFinite(qty) || qty <= 0) return null;
        return { packageKey, qty: Math.min(99, Math.trunc(qty)) } satisfies CartItem;
      })
      .filter(Boolean) as CartItem[];
  } catch {
    return [];
  }
}

function loadCart(): CartItem[] {
  return safeParseCart(globalThis?.localStorage?.getItem(CART_STORAGE_KEY) ?? null);
}

function clearCart() {
  try {
    globalThis?.localStorage?.setItem(CART_STORAGE_KEY, JSON.stringify([]));
  } catch {
    // ignore
  }
}

function safeParseProductCart(raw: string | null): ProductCartItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        if (typeof entry === "string") {
          const productId = entry.trim();
          return productId ? ({ productId, qty: 1 } satisfies ProductCartItem) : null;
        }

        const obj = entry as any;
        const productId = String(obj?.productId ?? "").trim();
        const qty = Math.max(1, Math.min(99, Math.trunc(Number(obj?.qty ?? 1) || 1)));
        if (!productId) return null;
        return { productId, qty } satisfies ProductCartItem;
      })
      .filter(Boolean) as ProductCartItem[];
  } catch {
    return [];
  }
}

function loadProductCart() {
  return safeParseProductCart(globalThis?.localStorage?.getItem(PRODUCT_CART_STORAGE_KEY) ?? null);
}

function clearProductCart() {
  try {
    globalThis?.localStorage?.setItem(PRODUCT_CART_STORAGE_KEY, JSON.stringify([]));
  } catch {
    // ignore
  }
}

function splitFullName(fullName: string) {
  const parts = String(fullName ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const ad = parts[0] ?? "";
  const soyad = parts.slice(1).join(" ");
  return { ad, soyad };
}

function normalizePackage(input: string | null): PackageKey {
  const v = (input ?? "").toLowerCase().trim();
  if (v === "pin" || v === "isim" || v === "normal" || v === "premium" || v === "iliski" || v === "test") {
    return v;
  }
  return "normal";
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ₺`;
  }
}

function getPackageEffectivePrice(pkg: CatalogResponse["packages"][number]) {
  return typeof pkg.discountedPriceTry === "number" && pkg.discountedPriceTry > 0 ? pkg.discountedPriceTry : pkg.basePriceTry;
}

function getProductEffectivePrice(product: CatalogResponse["products"][number]) {
  return typeof product.discountedPriceTry === "number" && product.discountedPriceTry > 0
    ? product.discountedPriceTry
    : product.priceTry;
}

function normalizeSearchText(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u");
}

function normalizeCategoryParent(value: string) {
  const key = normalizeSearchText(value).replace(/[^a-z0-9-]/g, "");
  if (["stone", "tas", "dogal-tas", "dogaltas"].includes(key)) return "stone";
  if (["oil", "yag", "ucucu-yag", "ucucuyag", "aroma-yag"].includes(key)) return "oil";
  if (["bundle", "paket", "set", "kombin"].includes(key)) return "bundle";
  if (["other", "diger"].includes(key)) return "other";
  return key || "other";
}

function normalizeSavedAddressLabel(input: unknown): SavedAddressLabel {
  return input === "ev" || input === "is" || input === "diger" ? input : "ev";
}

function formatSavedAddressLabel(label: SavedAddressLabel) {
  return label === "ev" ? "Ev" : label === "is" ? "İş Yeri" : "Diğer";
}

function loadSavedAddresses(userId: string | null | undefined): { addresses: SavedAddress[]; selectedId: string } {
  const normalizedUserId = String(userId ?? "").trim();
  if (!normalizedUserId) return { addresses: [], selectedId: "" };

  try {
    const raw = globalThis.localStorage?.getItem(PROFILE_EXTRAS_STORAGE_KEY) ?? "";
    if (!raw) return { addresses: [], selectedId: "" };

    const parsed = JSON.parse(raw) as any;
    const mine = parsed?.[normalizedUserId];

    const addresses = Array.isArray(mine?.saved_addresses)
      ? (mine.saved_addresses
          .map((entry: any) => {
            const id = String(entry?.id ?? "").trim();
            if (!id) return null;
            return {
              id,
              label: normalizeSavedAddressLabel(entry?.label),
              title: typeof entry?.title === "string" ? entry.title : "",
              identity_number: typeof entry?.identity_number === "string" ? entry.identity_number.replace(/[^0-9]/g, "").slice(0, 11) : "",
              phone: typeof entry?.phone === "string" ? entry.phone : "",
              address_line1: typeof entry?.address_line1 === "string" ? entry.address_line1 : "",
              city: typeof entry?.city === "string" ? entry.city : "",
              district: typeof entry?.district === "string" ? entry.district : "",
              postcode: typeof entry?.postcode === "string" ? entry.postcode : "",
            } satisfies SavedAddress;
          })
          .filter(Boolean) as SavedAddress[])
      : [];

    const selectedId = typeof mine?.selected_address_id === "string" ? mine.selected_address_id : "";

    return {
      addresses,
      selectedId: addresses.some((entry) => entry.id === selectedId) ? selectedId : addresses[0]?.id ?? "",
    };
  } catch {
    return { addresses: [], selectedId: "" };
  }
}

async function fetchCatalog(testMode: boolean): Promise<CatalogResponse> {
  const res = await fetch(testMode ? "/api/numeroloji/catalog?test=1" : "/api/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function createOrder(payload: any): Promise<CreateOrderResponse> {
  const res = await fetch("/api/numeroloji/orders", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Sipariş oluşturulamadı.");
  }
  return json as CreateOrderResponse;
}

export default function NumerolojiSatinAl() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSupabaseSession();
  const me = useMemo(() => getMe(), []);

  const testMode = (searchParams.get("test") ?? "").trim() === "1";

  const cartItems = useMemo(() => loadCart(), []);

  const digitalCartItem = useMemo(
    () => (cartItems.find((i: any) => i.digitalContent) as any)?.digitalContent ?? null,
    [cartItems],
  );

  const cartDefaultPackage = useMemo(() => {
    if (cartItems.length >= 1 && cartItems[0]?.packageKey) return String(cartItems[0].packageKey);
    return null;
  }, [cartItems]);

  const initialPackage = normalizePackage(searchParams.get("package") ?? cartDefaultPackage ?? (testMode ? "test" : null));

  const [selectedPackage, setSelectedPackage] = useState<PackageKey>(initialPackage);

  useEffect(() => {
    if (!testMode && selectedPackage === "test") {
      setSelectedPackage("normal");
    }
  }, [testMode, selectedPackage]);

  const [reportLang, setReportLang] = useState("tr");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const accountEmail = String(user?.email ?? "").trim();
  const emailLocked = Boolean(accountEmail);

  const accountName = useMemo(() => {
    const meta = ((user?.metadata ?? {}) as Record<string, unknown>) || {};
    const fullNameRaw =
      (typeof meta.full_name === "string" ? meta.full_name : "") ||
      (typeof meta.name === "string" ? meta.name : "") ||
      (typeof meta.display_name === "string" ? meta.display_name : "");

    const firstNameRaw = typeof meta.first_name === "string" ? meta.first_name : "";
    const lastNameRaw = typeof meta.last_name === "string" ? meta.last_name : "";

    const derived = fullNameRaw ? splitFullName(fullNameRaw) : { ad: "", soyad: "" };

    return {
      firstName: String(firstNameRaw || derived.ad || "").trim(),
      lastName: String(lastNameRaw || derived.soyad || "").trim(),
    };
  }, [user?.metadata]);

  const nameLocked = Boolean(accountName.firstName || accountName.lastName);

  useEffect(() => {
    if (!user) return;

    if (!firstName && accountName.firstName) setFirstName(accountName.firstName);
    if (!lastName && accountName.lastName) setLastName(accountName.lastName);

    if (accountEmail && email !== accountEmail) setEmail(accountEmail);
  }, [accountEmail, accountName.firstName, accountName.lastName, email, firstName, lastName, user]);
  const [phone, setPhone] = useState("");
  const [identityNumber, setIdentityNumber] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [postcode, setPostcode] = useState("");

  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState("");

  const addressOwnerId = useMemo(() => {
    if (user?.id) return String(user.id);
    const fallbackId = (me as any)?.id;
    return fallbackId ? String(fallbackId) : "";
  }, [me, user?.id]);

  useEffect(() => {
    const { addresses, selectedId } = loadSavedAddresses(addressOwnerId);
    setSavedAddresses(addresses);
    setSelectedSavedAddressId(selectedId);
  }, [addressOwnerId]);

  useEffect(() => {
    if (!selectedSavedAddressId) return;
    const selected = savedAddresses.find((entry) => entry.id === selectedSavedAddressId);
    if (!selected) return;

    setPhone(String(selected.phone ?? ""));
    setIdentityNumber(String(selected.identity_number ?? "").replace(/[^0-9]/g, "").slice(0, 11));
    setAddressLine1(String(selected.address_line1 ?? ""));
    setCity(String(selected.city ?? ""));
    setDistrict(String(selected.district ?? ""));
    setPostcode(String(selected.postcode ?? ""));
  }, [savedAddresses, selectedSavedAddressId]);

  const preselectedProductItems = useMemo(() => {
    const byList = (searchParams.get("productIds") ?? "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    const single = (searchParams.get("productId") ?? "").trim();
    const fromStorage = loadProductCart();

    const qtyById = new Map<string, number>();

    for (const item of fromStorage) {
      const id = String(item.productId ?? "").trim();
      if (!id) continue;
      qtyById.set(id, Math.max(1, Math.min(99, Math.trunc(Number(item.qty ?? 1) || 1))));
    }

    for (const id of [...byList, ...(single ? [single] : [])]) {
      if (!qtyById.has(id)) qtyById.set(id, 1);
    }

    return Array.from(qtyById.entries()).map(([productId, qty]) => ({ productId, qty }));
  }, [searchParams]);

  const [selectedAddOns, setSelectedAddOns] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const row of preselectedProductItems) initial[row.productId] = true;
    return initial;
  });

  useEffect(() => {
    if (!preselectedProductItems.length) return;
    setSelectedAddOns((prev) => {
      const next = { ...prev };
      for (const row of preselectedProductItems) next[row.productId] = true;
      return next;
    });
  }, [preselectedProductItems]);

  const catalogQuery = useQuery({
    queryKey: ["numeroloji-catalog", testMode],
    queryFn: () => fetchCatalog(testMode),
    staleTime: 60_000,
  });

  const packages = catalogQuery.data?.packages ?? [];
  const products = catalogQuery.data?.products ?? [];

  const cartPackagesResolved = useMemo(() => {
    if (!cartItems.length) return [];
    const byKey = new Map(packages.map((p) => [p.key, p] as const));

    return cartItems
      .map((it) => {
        const key = normalizePackage(String(it.packageKey));
        const pkg = byKey.get(key);
        if (!pkg) return null;
        const qty = Math.max(1, Math.min(99, Math.trunc(Number(it.qty ?? 1) || 1)));
        return { pkg, qty };
      })
      .filter(Boolean) as Array<{ pkg: CatalogResponse["packages"][number]; qty: number }>;
  }, [cartItems, packages]);

  const usingCartPackages = cartPackagesResolved.length > 0;
  const hasExplicitPackageParam = Boolean((searchParams.get("package") ?? "").trim());
  const isProductOnlyCheckout = !usingCartPackages && preselectedProductItems.length > 0 && !hasExplicitPackageParam;

  const selectedPkgInfo = useMemo(() => {
    if (usingCartPackages || isProductOnlyCheckout) return null;
    return packages.find((p) => p.key === selectedPackage) ?? null;
  }, [isProductOnlyCheckout, packages, selectedPackage, usingCartPackages]);

  const addOnList = useMemo(() => {
    const selectedIds = new Set(Object.keys(selectedAddOns).filter((id) => selectedAddOns[id]));
    return products.filter((p) => {
      if (selectedIds.has(p.id)) return true;
      const parent = normalizeCategoryParent(String(p.category ?? "").split(/[/>]/)[0] ?? "");
      return parent === "stone" || parent === "oil" || parent === "bundle";
    });
  }, [products, selectedAddOns]);

  const selectedProductQtyById = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of preselectedProductItems) {
      map[row.productId] = row.qty;
    }
    return map;
  }, [preselectedProductItems]);

  const selectedProductList = useMemo(() => {
    return products.filter((p) => Boolean(selectedAddOns[p.id]));
  }, [products, selectedAddOns]);

  const lineItems = useMemo(() => {
    const items: Array<{ id: string; title: string; priceTry: number }> = [];

    if (digitalCartItem) {
      items.push({ id: `digital:${digitalCartItem.contentId}`, title: digitalCartItem.title, priceTry: digitalCartItem.price });
      return items;
    }

    if (usingCartPackages) {
      for (const row of cartPackagesResolved) {
        items.push({
          id: `pkg:${row.pkg.key}`,
          title: row.qty > 1 ? `${row.pkg.name} ×${row.qty}` : row.pkg.name,
          priceTry: getPackageEffectivePrice(row.pkg) * row.qty,
        });
      }
    } else if (selectedPkgInfo) {
      items.push({ id: `pkg:${selectedPkgInfo.key}`, title: selectedPkgInfo.name, priceTry: getPackageEffectivePrice(selectedPkgInfo) });
    }

    for (const p of selectedProductList) {
      const qty = Math.max(1, Math.min(99, Math.trunc(Number(selectedProductQtyById[p.id] ?? 1) || 1)));
      items.push({
        id: `product:${p.id}`,
        title: qty > 1 ? `${p.name} ×${qty}` : p.name,
        priceTry: getProductEffectivePrice(p) * qty,
      });
    }

    return items;
  }, [cartPackagesResolved, selectedPkgInfo, selectedProductList, selectedProductQtyById, usingCartPackages]);

  const totalPreview = useMemo(() => {
    let sum = 0;
    for (const it of lineItems) sum += Number.isFinite(it.priceTry) ? it.priceTry : 0;
    return sum;
  }, [lineItems]);

  const vatBreakdown = useMemo(() => {
    const gross = Math.max(0, Number(totalPreview) || 0);
    const net = Math.round(gross / (1 + VAT_RATE));
    const vat = Math.max(0, gross - net);
    return { gross, net, vat };
  }, [totalPreview]);

  const mutation = useMutation({
    mutationFn: createOrder,
    onSuccess: (data) => {
      // Sipariş oluşturulduktan sonra sepeti boşalt.
      // (Kullanıcı geri dönerse sepet dolu kalmasın.)
      clearCart();
      clearProductCart();
      setCartCount(0);

      const url = new URL("/numeroloji/odeme", window.location.origin);
      url.searchParams.set("orderId", data.access.orderId);
      url.searchParams.set("token", data.access.token);

      const checkoutPackageKey = usingCartPackages ? cartPackagesResolved[0]?.pkg.key : selectedPkgInfo?.key;
      if (checkoutPackageKey && (!usingCartPackages || cartPackagesResolved.length === 1)) {
        url.searchParams.set("package", String(checkoutPackageKey));
      }

      url.searchParams.set("lang", reportLang);
      navigate(url.pathname + url.search);
    },
  });

  const [cartCount, setCartCount] = useState(() => loadCart().reduce((sum, it) => sum + (Number.isFinite(it.qty) ? it.qty : 0), 0));

  useEffect(() => {
    const onStorage = () => {
      setCartCount(loadCart().reduce((sum, it) => sum + (Number.isFinite(it.qty) ? it.qty : 0), 0));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <div className="min-h-dvh bg-[#050508] text-slate-200">
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(circle at 50% 40%, rgba(127,13,242,0.14), transparent 55%), radial-gradient(circle at 20% 30%, rgba(212,175,55,0.10), transparent 48%), radial-gradient(circle at 80% 70%, rgba(212,175,55,0.08), transparent 55%)",
        }}
      />

      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#050508]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
          <Link to="/numeroloji" className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d4af37] rotate-45">
              <span className="h-3 w-3 bg-[#d4af37]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[0.16em] text-white">Numeroloji.app</span>
          </Link>

          <div className="flex items-center gap-6 sm:gap-8">
            <Link
              to="/numeroloji/magaza"
              className="font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:text-sm"
            >
              MAĞAZA
            </Link>
            <Link
              to="/numeroloji#kasalar"
              className="hidden font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:block sm:text-sm"
            >
              ANALİZLER
            </Link>

            <Link to="/numeroloji/sepet" className="relative">
              <ShoppingBag className="h-5 w-5 text-slate-200" />
              {cartCount ? (
                <span
                  className="absolute -right-2 -top-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-black"
                  style={{ backgroundColor: GOLD }}
                  aria-label={`Sepette ${cartCount} ürün`}
                >
                  {cartCount}
                </span>
              ) : null}
            </Link>
          </div>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-10">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Güvenli Ödeme</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            <Link to="/numeroloji" className="transition-colors hover:text-[rgba(212,175,55,0.95)]">
              Ana Sayfa
            </Link>
            <ChevronRight className="h-4 w-4 opacity-60" />
            <Link to="/numeroloji/sepet" className="transition-colors hover:text-[rgba(212,175,55,0.95)]">
              Sepetim
            </Link>
            <ChevronRight className="h-4 w-4 opacity-60" />
            <span style={{ color: GOLD }}>Ödeme</span>
          </div>
        </div>

        {catalogQuery.isLoading ? (
          <div className="rounded-2xl border p-6 text-sm text-slate-300" style={{ borderColor: "rgba(212,175,55,0.12)" }}>
            Yükleniyor...
          </div>
        ) : catalogQuery.isError ? (
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
            {(catalogQuery.error as Error).message}
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (mutation.isPending) return;

              const productItems = selectedProductList.map((p) => ({
                productId: p.id,
                qty: Math.max(1, Math.min(99, Math.trunc(Number(selectedProductQtyById[p.id] ?? 1) || 1))),
              }));

              const packageItems = usingCartPackages
                ? cartPackagesResolved.map((r) => ({ packageKey: r.pkg.key, qty: r.qty }))
                : undefined;

              const primaryPackageKey = usingCartPackages ? cartPackagesResolved[0]?.pkg.key : selectedPkgInfo?.key;

              mutation.mutate({
                packageKey: primaryPackageKey,
                packageItems,
                testMode,
                productItems,
                firstName,
                lastName,
                email,
                phone,
                identityNumber,
                addressLine1,
                city,
                district,
                postcode,
                ...(digitalCartItem ? { digitalContent: digitalCartItem } : {}),
              });
            }}
          >
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
              <section
                className="lg:col-span-7 rounded-3xl border p-6 shadow-2xl sm:p-8"
                style={{
                  borderColor: "rgba(212,175,55,0.18)",
                  background:
                    "radial-gradient(circle at 20% 20%, rgba(212,175,55,0.10) 0%, transparent 60%), linear-gradient(145deg, rgba(26,18,37,0.55), rgba(10,10,12,0.70))",
                }}
              >
                <div className="flex items-center gap-3 border-b border-[rgba(212,175,55,0.14)] pb-4">
                  <ReceiptText className="h-5 w-5" style={{ color: GOLD }} />
                  <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">Fatura Bilgileri</h2>
                </div>

                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="firstName" className="text-sm text-slate-300">
                      Ad
                    </Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => {
                        if (nameLocked) return;
                        setFirstName(e.target.value);
                      }}
                      required
                      readOnly={nameLocked}
                      className="h-11 border-[rgba(212,175,55,0.22)] bg-black/25 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)] read-only:opacity-80"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="lastName" className="text-sm text-slate-300">
                      Soyad
                    </Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => {
                        if (nameLocked) return;
                        setLastName(e.target.value);
                      }}
                      required
                      readOnly={nameLocked}
                      className="h-11 border-[rgba(212,175,55,0.22)] bg-black/25 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)] read-only:opacity-80"
                    />
                    {nameLocked ? <div className="text-xs text-slate-500 sm:col-span-2">Ad/Soyad hesabınızdan alınır.</div> : null}
                  </div>

                  <div className="grid gap-2 sm:col-span-2">
                    <Label htmlFor="email" className="text-sm text-slate-300">
                      E-posta
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        if (emailLocked) return;
                        setEmail(e.target.value);
                      }}
                      required
                      readOnly={emailLocked}
                      className="h-11 border-[rgba(212,175,55,0.22)] bg-black/25 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)] read-only:opacity-80"
                      placeholder="ornek@mail.com"
                    />
                    {emailLocked ? <div className="text-xs text-slate-500">E-posta hesabınızdan alınır ve değiştirilemez.</div> : null}
                  </div>

                  {savedAddresses.length ? (
                    <div className="grid gap-2 sm:col-span-2">
                      <Label htmlFor="savedAddress" className="text-sm text-slate-300">
                        Kayıtlı Adresler
                      </Label>
                      <select
                        id="savedAddress"
                        value={selectedSavedAddressId}
                        onChange={(e) => setSelectedSavedAddressId(e.target.value)}
                        className="h-11 w-full rounded-md border border-[rgba(212,175,55,0.22)] bg-black/25 px-3 text-sm text-slate-100 outline-none focus:border-[rgba(212,175,55,0.55)]"
                      >
                        <option value="">Manuel gir</option>
                        {savedAddresses.map((entry) => {
                          const title = String(entry.title ?? "").trim();
                          const cityText = String(entry.city ?? "").trim();
                          const suffix = cityText ? ` • ${cityText}` : "";
                          return (
                            <option key={entry.id} value={entry.id}>
                              {title || formatSavedAddressLabel(entry.label)}{suffix}
                            </option>
                          );
                        })}
                      </select>
                      <div className="text-xs text-slate-500">Profilde kayıtlı ev/iş adreslerinden birini seçebilirsiniz.</div>
                    </div>
                  ) : null}

                  <div className="grid gap-2 sm:col-span-2">
                    <Label htmlFor="phone" className="text-sm text-slate-300">
                      Telefon
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      className="h-11 border-[rgba(212,175,55,0.22)] bg-black/25 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)]"
                      placeholder={"+90 5XX XXX XX XX"}
                    />
                  </div>

                  <div className="grid gap-2 sm:col-span-2">
                    <Label htmlFor="identityNumber" className="text-sm text-slate-300">
                      TCKN
                    </Label>
                    <Input
                      id="identityNumber"
                      inputMode="numeric"
                      value={identityNumber}
                      onChange={(e) => setIdentityNumber(e.target.value.replace(/[^0-9]/g, ""))}
                      minLength={11}
                      maxLength={11}
                      required
                      className="h-11 border-[rgba(212,175,55,0.22)] bg-black/25 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)]"
                      placeholder="11 haneli"
                    />
                    <div className="text-xs text-slate-500">Ödeme altyapısı için gereklidir.</div>
                  </div>

                  <div className="grid gap-2 sm:col-span-2">
                    <Label htmlFor="addressLine1" className="text-sm text-slate-300">
                      Adres
                    </Label>
                    <Textarea
                      id="addressLine1"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      required
                      rows={4}
                      className="min-h-[92px] resize-none border-[rgba(212,175,55,0.22)] bg-black/25 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)]"
                      placeholder="Açık adresiniz..."
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="city" className="text-sm text-slate-300">
                      Şehir
                    </Label>
                    <Input
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                      className="h-11 border-[rgba(212,175,55,0.22)] bg-black/25 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)]"
                      placeholder="İstanbul"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="postcode" className="text-sm text-slate-300">
                      Posta Kodu
                    </Label>
                    <Input
                      id="postcode"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                      className="h-11 border-[rgba(212,175,55,0.22)] bg-black/25 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)]"
                      placeholder="34000"
                    />
                  </div>

                  <div className="grid gap-2 sm:col-span-2">
                    <Label htmlFor="district" className="text-sm text-slate-300">
                      İlçe (opsiyonel)
                    </Label>
                    <Input
                      id="district"
                      value={district}
                      onChange={(e) => setDistrict(e.target.value)}
                      className="h-11 border-[rgba(212,175,55,0.22)] bg-black/25 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)]"
                      placeholder="Kadıköy"
                    />
                  </div>

                  {digitalCartItem ? (
                    <div className="grid gap-2 sm:col-span-2">
                      <Label className="text-sm text-slate-300">Dijital İçerik</Label>
                      <div className="rounded-md border border-[rgba(212,175,55,0.22)] bg-black/25 p-3 text-sm text-slate-100">
                        <div className="flex items-center justify-between gap-3">
                          <span className="min-w-0 flex-1 truncate">{digitalCartItem.title}</span>
                          <span className="shrink-0 text-slate-400">{formatTry(digitalCartItem.price)}</span>
                        </div>
                      </div>
                      <div className="text-xs text-slate-500">Satın alma tamamlandığında Soul Circles hesabınıza tanımlanır.</div>
                    </div>
                  ) : usingCartPackages ? (
                    <div className="grid gap-2">
                      <Label className="text-sm text-slate-300">Sepetiniz</Label>
                      <div className="rounded-md border border-[rgba(212,175,55,0.22)] bg-black/25 p-3 text-sm text-slate-100">
                        <div className="grid gap-1">
                          {cartPackagesResolved.map((r) => (
                            <div key={r.pkg.id} className="flex items-start justify-between gap-3">
                              <span className="min-w-0 flex-1 truncate">{r.pkg.name}</span>
                              <span className="shrink-0 text-slate-400">{formatTry(getPackageEffectivePrice(r.pkg))}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="text-xs text-slate-500">Ödeme toplamı sepetinizdeki paketlerin tamamı içindir.</div>
                    </div>
                  ) : isProductOnlyCheckout ? (
                    <div className="grid gap-2 sm:col-span-2">
                      <Label className="text-sm text-slate-300">Analiz</Label>
                      <div className="rounded-md border border-[rgba(212,175,55,0.22)] bg-black/25 p-3 text-sm text-slate-100">
                        Bu siparişte yalnızca fiziksel ürünler var. Numeroloji analiz formu bu siparişe eklenmeyecek.
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-2">
                      <Label className="text-sm text-slate-300">Paket</Label>
                      <select
                        className="h-11 w-full rounded-md border border-[rgba(212,175,55,0.22)] bg-black/25 px-3 text-sm text-slate-100 outline-none focus:border-[rgba(212,175,55,0.55)]"
                        value={selectedPackage}
                        onChange={(e) => setSelectedPackage(normalizePackage(e.target.value))}
                      >
                        {packages.map((p) => (
                          <option key={p.id} value={p.key}>
                            {p.name} ({formatTry(getPackageEffectivePrice(p))})
                          </option>
                        ))}
                      </select>
                      <div className="text-xs text-slate-500">{selectedPkgInfo?.description ?? ""}</div>
                    </div>
                  )}

                  {!digitalCartItem && (
                    <div className="grid gap-2">
                      <Label className="text-sm text-slate-300">Rapor dili</Label>
                      <select
                        className="h-11 w-full rounded-md border border-[rgba(212,175,55,0.22)] bg-black/25 px-3 text-sm text-slate-100 outline-none focus:border-[rgba(212,175,55,0.55)]"
                        value={reportLang}
                        onChange={(e) => setReportLang(e.target.value)}
                      >
                        <option value="tr">Türkçe</option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="de">Deutsch</option>
                      </select>
                    </div>
                  )}
                </div>

                {!digitalCartItem && addOnList.length ? (
                  <div className="mt-10 rounded-2xl border border-[rgba(212,175,55,0.12)] bg-black/20 p-5">
                    <div className="text-sm font-semibold text-white">Ek ürünler (opsiyonel)</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Numerolojik ihtiyaca göre doğal taş ve aromaterapi yağları gibi ürünler ekleyebilirsiniz.
                    </div>

                    <div className="mt-4 grid gap-3">
                      {addOnList.map((p) => (
                        <label
                          key={p.id}
                          className="flex items-start gap-3 rounded-xl border border-[rgba(212,175,55,0.14)] bg-black/20 p-3"
                        >
                          <Checkbox
                            checked={Boolean(selectedAddOns[p.id])}
                            onCheckedChange={(checked) => {
                              setSelectedAddOns((prev) => ({ ...prev, [p.id]: Boolean(checked) }));
                            }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="font-medium text-slate-100">{p.name}</div>
                              <div className="text-sm text-slate-400">{formatTry(getProductEffectivePrice(p))}</div>
                            </div>
                            <div className="mt-1 text-xs text-slate-500 line-clamp-2">{p.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>

              <aside className="lg:col-span-5">
                <section
                  className="rounded-3xl border p-6 shadow-2xl sm:p-8"
                  style={{
                    borderColor: "rgba(212,175,55,0.18)",
                    background:
                      "radial-gradient(circle at 80% 25%, rgba(212,175,55,0.10) 0%, transparent 60%), linear-gradient(145deg, rgba(26,18,37,0.55), rgba(10,10,12,0.70))",
                  }}
                >
                  <div className="flex items-center justify-between gap-4 border-b border-[rgba(212,175,55,0.14)] pb-4">
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5" style={{ color: GOLD }} />
                      <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">Ödeme Yöntemi</h2>
                    </div>
                    <div className="hidden items-center gap-2 text-xs text-slate-500 sm:flex">
                      <ShieldCheck className="h-4 w-4" />
                      <span>Güvenli yönlendirme</span>
                    </div>
                  </div>

                  <div className="mt-8 rounded-2xl border border-[rgba(212,175,55,0.12)] bg-black/20 p-5">
                    <div className="text-sm font-semibold text-white">iyzico ile güvenli ödeme</div>
                    <p className="mt-2 text-sm text-slate-400">
                      Kart bilgilerinizi bu sayfada istemiyoruz. “Ödemeyi Tamamla” dediğinizde iyzico ödeme ekranına
                      yönlendirilirsiniz.
                    </p>
                  </div>

                  <div className="mt-10 rounded-2xl border border-[rgba(212,175,55,0.12)] bg-black/20 p-5">
                    <div className="text-sm font-semibold text-white">Sipariş Özeti</div>

                    <div className="mt-4 space-y-3 text-sm">
                      {lineItems.length ? (
                        lineItems.map((it) => (
                          <div key={it.id} className="flex items-start justify-between gap-4 text-slate-400">
                            <span className="min-w-0 flex-1 truncate">{it.title}</span>
                            <span className="shrink-0">{formatTry(it.priceTry)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-slate-500">Ürün bulunamadı.</div>
                      )}

                      <div className="border-t border-[rgba(212,175,55,0.12)] pt-4">
                        <div className="grid gap-2 text-sm">
                          <div className="flex items-center justify-between text-slate-400">
                            <span>Ara Toplam</span>
                            <span>{formatTry(vatBreakdown.net)}</span>
                          </div>
                          <div className="flex items-center justify-between text-slate-400">
                            <span>KDV (%20)</span>
                            <span>{formatTry(vatBreakdown.vat)}</span>
                          </div>

                          <div className="mt-2 flex items-center justify-between">
                            <span className="font-semibold text-slate-100">Genel Toplam (KDV dahil)</span>
                            <span className="text-xl font-semibold" style={{ color: GOLD }}>
                              {formatTry(vatBreakdown.gross)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {mutation.isError ? (
                    <div className="mt-6 rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
                      {(mutation.error as Error).message}
                    </div>
                  ) : null}

                  <Button
                    type="submit"
                    className="mt-7 h-12 w-full rounded-xl font-display text-xs font-bold uppercase tracking-[0.22em] text-black"
                    style={{ backgroundColor: GOLD }}
                    disabled={mutation.isPending}
                  >
                    <span className="mr-2">{mutation.isPending ? "İşleniyor..." : "Ödemeyi Tamamla"}</span>
                    <LockKeyhole className="h-4 w-4" />
                  </Button>

                  <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
                    <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                      <ShieldCheck className="h-4 w-4" />
                      256-Bit SSL
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                      <LockKeyhole className="h-4 w-4" />
                      Güvenli Ödeme
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                      <Sparkles className="h-4 w-4" />
                      %100 Orijinal Analiz
                    </div>
                  </div>

                  <div className="mt-6 text-center text-xs leading-relaxed text-slate-500">
                    Sipariş oluşturulur ve ardından iyzico ile güvenli ödeme adımına yönlendirilirsiniz.
                  </div>
                </section>
              </aside>
            </div>
          </form>
        )}
      </main>

      <footer className="mt-20 border-t py-12" style={{ borderColor: "rgba(212,175,55,0.10)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4">
          <div className="h-24 w-px bg-gradient-to-b from-[rgba(212,175,55,0.95)] to-transparent" />
          <div className="text-center">
            <div className="font-display text-3xl tracking-widest" style={{ color: GOLD }}>
              Numeroloji.app
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-40">
              Evrensel Sayılar Rehberi © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
